import { useState } from 'react';
import { MapPin, DollarSign, Calendar, User, Eye, UserPlus, MoreVertical, CheckCircle, XCircle, Clock, Trash2 } from 'lucide-react';
import { LeadDetailsModal } from './LeadDetailsModal';
import { AssignContractorModal } from './AssignContractorModal';

interface Lead {
  id: string;
  title: string;
  category: string;
  tradesmens_commission: string;
  description: string;
  budget: number;
  location: {
    address: string;
    city: string;
    state: string;
    zipCode: string;
  };
  lead_sub_cat: string;
  postedDate: string;
  status: string;
  homeowner: {
    id: number;
    name: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    pic: string;
    memberSince: string;
    completedProjects: number;
  };
  images: string[];
  media: Array<{
    type: string;
    url: string;
  }>;
  activities?: Array<{
    id: string;
    type: 'status_change' | 'assigned' | 'note' | 'created';
    description: string;
    timestamp: string;
    user: string;
  }>;
}

interface LeadCardProps {
  lead: Lead;
  onUpdateLead: (leadId: string, updates: Partial<Lead>) => void;
  onDeleteLead: (leadId: string) => void;
  setRefresh:any
}

export function LeadCard({ lead, onUpdateLead, onDeleteLead,setRefresh }: LeadCardProps) {
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [showActions, setShowActions] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'declined':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'active':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'pending':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'assigned':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'completed':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleAssignContractor = (leadId: string, contractorId: string) => {
    setRefresh((r:any)=>!r)
    
  };

  const handleStatusChange = (newStatus: string) => {
    onUpdateLead(lead.id, { status: newStatus });
    setShowActions(false);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
      onDeleteLead(lead.id);
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-all all-leads-card">
        {/* Image Header */}
        <div className="relative h-40 bg-gradient-to-br from-gray-100 to-gray-200">
          {lead.media && lead.media[0] && (
            <img
              src={lead.media[0].url}
              alt={lead.title}
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute top-3 right-3 flex gap-2">
            <span className={`${getStatusColor(lead.status)} border px-2.5 py-1 rounded-md text-xs font-medium shadow-sm`}>
              {lead.status.charAt(0).toUpperCase() + lead.status.slice(1)}
            </span>
          </div>
          <div className="absolute top-3 left-3">
            <span className="bg-white bg-opacity-90 text-gray-700 px-2.5 py-1 rounded-md text-xs font-medium">
              #{lead.id}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <h3 className="text-base font-medium text-gray-900 mb-1">{lead.title}</h3>
              <p className="text-xs text-gray-500">{lead.category} • {lead.lead_sub_cat}</p>
            </div>
          </div>

          <p className="text-gray-600 text-xs mb-3 line-clamp-2">{lead.description}</p>

          {/* Budget & Commission */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="bg-blue-50 rounded-lg p-2.5 border border-blue-100">
              <div className="flex items-center gap-1 text-blue-600 text-xs mb-1">
                <DollarSign className="w-3 h-3" />
                <span>Budget</span>
              </div>
              <p className="text-base font-medium text-blue-700">${lead.budget.toLocaleString()}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-2.5 border border-green-100">
              <div className="flex items-center gap-1 text-green-600 text-xs mb-1">
                <DollarSign className="w-3 h-3" />
                <span>Commission</span>
              </div>
              <p className="text-base font-medium text-green-700">${lead.tradesmens_commission}</p>
            </div>
          </div>

          {/* Location */}
          <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-2">
            <MapPin className="w-3 h-3 text-gray-400" />
            <span>{lead.location.city}, {lead.location.state} {lead.location.zipCode}</span>
          </div>

          {/* Homeowner */}
          <div className="flex items-center gap-2 text-xs text-gray-600 mb-2 bg-gray-50 rounded-lg p-2.5 border border-gray-100">
            <User className="w-3 h-3 text-gray-400" />
            <div className="flex-1">
              <p className="font-medium text-gray-700">{lead.homeowner.name}</p>
              <p className="text-xs text-gray-500">{lead.homeowner.phone}</p>
            </div>
          </div>

          {/* Posted Date */}
          <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3">
            <Calendar className="w-3 h-3" />
            <span>Posted {formatDate(lead.postedDate)}</span>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={() => setIsDetailsModalOpen(true)}
              className="flex-1 border-0 flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              <Eye className="w-3.5 h-3.5" />
              View
            </button>
            <button
              onClick={() => setIsAssignModalOpen(true)}
              className="flex-1 border-0 flex items-center justify-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
            >
              <UserPlus className="w-3.5 h-3.5" />
              Assign
            </button>
            <div className="relative">
              <button
                onClick={() => setShowActions(!showActions)}
                className="px-3 border-0 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <MoreVertical className="w-3.5 h-3.5" />
              </button>
              {showActions && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowActions(false)}
                  ></div>
                  <div className="absolute right-0 mt-1 w-44 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                    <div className="py-1">
                      <p className="px-3 py-2 text-xs text-gray-500 uppercase font-medium">Change Status</p>
                      <button
                        onClick={() => handleStatusChange('active')}
                        className="w-full border-0 text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        Active
                      </button>
                      <button
                        onClick={() => handleStatusChange('pending')}
                        className="w-full border-0 text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <Clock className="w-4 h-4 text-amber-600" />
                        Pending
                      </button>
                      <button
                        onClick={() => handleStatusChange('declined')}
                        className="w-full border-0 text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <XCircle className="w-4 h-4 text-red-600" />
                        Declined
                      </button>
                      <button
                        onClick={() => handleStatusChange('completed')}
                        className="w-full border-0 text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <CheckCircle className="w-4 h-4 text-purple-600" />
                        Completed
                      </button>
                      <div className="border-t my-1"></div>
                      <button
                        onClick={handleDelete}
                        className="w-full border-0 text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Lead
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <LeadDetailsModal
        lead={lead}
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
      />

      <AssignContractorModal
        leadId={lead.id}
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        onAssign={handleAssignContractor}
      />
    </>
  );
}
